import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { PREFERENCIAS_PADRAO, preferenciasGerais, type PreferenciasGerais } from '@dv/contracts'
import { EVENTO_DE_PREFERENCIAS } from '../api/evento-preferencias.ts'
import { formatarDataNominal, formatarInstante, horaDoInstante } from '../datas.ts'

const ContextoDePreferencias = createContext<PreferenciasGerais>(PREFERENCIAS_PADRAO)

export function usePreferencias(): PreferenciasGerais {
  return useContext(ContextoDePreferencias)
}

export interface DatasDoUsuario {
  /** `YYYY-MM-DD` no formato escolhido. NÃO recebe fuso: data nominal não muda de dia. */
  readonly nominal: (data: string, opcoes?: { compacta?: boolean }) => string | null
  /** Instante ISO no FUSO e no formato escolhidos. */
  readonly instante: (iso: string, opcoes?: { hora?: boolean; compacta?: boolean }) => string | null
  /** Só o relógio de um instante, no fuso escolhido. */
  readonly hora: (iso: string) => string | null
}

/**
 * As duas preferências temporais já amarradas ao usuário da sessão.
 *
 * Um hook, e não um `usePreferencias()` seguido de `formatarX(..., p.formatoDeData)` em
 * cada chamada: o segundo obriga quem escreve a lembrar de passar o fuso — e quem
 * esquece de passá-lo numa data NOMINAL não recebe erro, recebe um dia a menos.
 * Aqui a data nominal simplesmente não tem onde receber fuso.
 */
export function useDatas(): DatasDoUsuario {
  const { formatoDeData, fusoHorario } = usePreferencias()
  return useMemo(() => ({
    nominal: (data: string, opcoes?: { compacta?: boolean }) => formatarDataNominal(data, formatoDeData, opcoes),
    instante: (iso: string, opcoes?: { hora?: boolean; compacta?: boolean }) =>
      formatarInstante(iso, fusoHorario, formatoDeData, opcoes),
    hora: (iso: string) => horaDoInstante(iso, fusoHorario),
  }), [formatoDeData, fusoHorario])
}

/** Não remonta páginas ao salvar: um rascunho de outro cartão continua intacto. */
export function ProvedorDePreferencias({ preferencias, children }: {
  preferencias: PreferenciasGerais | undefined
  children: ReactNode
}) {
  const [salvas, setSalvas] = useState<PreferenciasGerais>()
  const atuais = salvas ?? preferencias ?? PREFERENCIAS_PADRAO

  useEffect(() => {
    const atualizar = (evento: Event): void => {
      if (!(evento instanceof CustomEvent)) return
      const resultado = preferenciasGerais.safeParse(evento.detail)
      if (resultado.success) setSalvas(resultado.data)
    }
    window.addEventListener(EVENTO_DE_PREFERENCIAS, atualizar)
    return () => { window.removeEventListener(EVENTO_DE_PREFERENCIAS, atualizar) }
  }, [])

  useEffect(() => {
    const raiz = document.documentElement
    const sistema = window.matchMedia('(prefers-color-scheme: dark)')
    const aplicar = (): void => {
      const escuro = atuais.tema === 'escuro' || (atuais.tema === 'automatico' && sistema.matches)
      raiz.dataset['tema'] = escuro ? 'escuro' : 'claro'
      raiz.style.colorScheme = escuro ? 'dark' : 'light'
    }
    aplicar()
    sistema.addEventListener('change', aplicar)
    return () => {
      sistema.removeEventListener('change', aplicar)
      delete raiz.dataset['tema']
      raiz.style.removeProperty('color-scheme')
    }
  }, [atuais.tema])

  return <ContextoDePreferencias.Provider value={atuais}>{children}</ContextoDePreferencias.Provider>
}
